import type { IUserSettingsUdfTypeOption } from 'app/services/userSettingsApi';

/**
 * The three popups this tab owns — two from U-c, one (the format-rail delete)
 * from U-d. All three are `self.$el.append(...)`ed into `#submittal_format` —
 * **not portalled to `<body>`** — and all three then get `.active` added by
 * hand, which is what `styles.css` and the global Esc handler key off. Rendered
 * as children of the submittal pane here for the same reason.
 *
 * ★ U-e — TWO OF THE THREE ARE SHARED WITH THE PROPOSAL TAB, BECAUSE THEY ARE
 * SHARED IN LEGACY. `tmpl/delete.udf.tmpl` and `tmpl/delete.format.field.tmpl`
 * live in the app-level `mvc/tmpl/` directory, not under `submittal/tmpl/`, and
 * `proposal.js:18-19` requires the very same two files. The proposal view feeds
 * the second one `type: "Proposal"` (`:466`) where submittal feeds it
 * `"Submittal"` (`submittal.js:643`) — hence the `type` prop below. This is the
 * ONE place the two tabs legitimately share a component; the views, the rails,
 * the libraries and the detail panes are transcribed twice on purpose.
 * (`select.udf.type.tmpl` is submittal-only — the Proposal tab has no type
 * popup.)
 *
 * ★ Esc / Enter are NOT bound here. `view/main.js:40-50` installs a global
 * `body.keyup` (and `submittal.js:151-161` installs an identical SECOND copy,
 * and proposal a third — Q17) where Esc removes every `.popup-wrapper.active`
 * and Enter clicks `button[autofocus="true"]`, which no popup template sets, so
 * Enter is inert. That is page-shell behaviour: wave U-f mounts
 * `usePopupKeys({blanket: true})` on `UserSettingsPage`, and all THREE popups
 * below are dismissed by it through the `button[name="cancel_delete"]` they
 * expose — one of that hook's cancel selectors. Do NOT add `autoFocus` to any
 * button here: it would make Enter confirm a delete that legacy's Enter cannot
 * (measured: zero matching buttons on the live page, with both Q4 popups open).
 */

// ───────────────────────────────────────────────────────────────────────────
// `tmpl/select.udf.type.tmpl` — "Select type of User Defined Field"
// ───────────────────────────────────────────────────────────────────────────

interface UdfTypePopupProps {
  /** `collectionSubmittalTypeFormat.toJSON()` — the two
   * `MasterUserDefinedFieldOptions` rows. */
  options: IUserSettingsUdfTypeOption[];
  /** `change [name="udf_format"]` → `changeUDFTypeFormat` (`:294-297`), which
   * stores `e.target.dataset.id` — a STRING. */
  onTypeChange: (dataId: string) => void;
  /** `click [name="done_type_format"]` → `addUserDefinedField` (§6 W1). */
  onAdd: () => void;
  /** `click button[name="cancel_delete"]` → `remove_confirm_popup`, which reads
   * the button's `data-parent` and removes `#popup-content<that class>`. */
  onCancel: () => void;
}

export const UdfTypePopup = ({ options, onTypeChange, onAdd, onCancel }: UdfTypePopupProps) => (
  <div id="popup-content" className="popup-wrapper width-small add-file active">
    <div className="popup-content">
      <h2 className="popup-heading">Select type of User Defined Field</h2>
      <div className="popup-content-part">
        <form className="submittal_udf_type_format_form">
          {options.map((option) => (
            /* `class="udf_format_content order_{{#ifCompare value '==' 'pdf'}}1{{/ifCompare}}"`
               — literally `order_1` for PDF and a bare `order_` for text. */
            <div key={option.id} className={`udf_format_content order_${option.value === 'pdf' ? '1' : ''}`}>
              {/* ★ PDF is the template's `checked` radio, which is why an
                  untouched popup POSTs the NUMBER 2 that
                  `openUserDefinedFieldPopUp` seeded — the radio's `change`
                  never fires, so `changeUDFTypeFormat` never overwrites it with
                  the string form. `defaultChecked` reproduces that: the input
                  is uncontrolled, so selecting it after selecting the other one
                  DOES fire `change` and DOES switch to the string, exactly like
                  legacy. */}
              <input
                type="radio"
                data-id={option.id}
                data-name={`${option.value}_format`}
                name="udf_format"
                value={option.value}
                id={option.value}
                defaultChecked={option.value === 'pdf'}
                onChange={() => onTypeChange(String(option.id))}
              />
              <label htmlFor={option.value} className="udf_format">
                {option.name}
              </label>
            </div>
          ))}
        </form>
      </div>
      <div className="popup-control-buttons">
        <button className="btn highlighted-btn" name="done_type_format" onClick={onAdd}>
          ADD
        </button>
        <button
          className="btn not-highlighted-btn"
          name="cancel_delete"
          data-parent=".add-file"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// `tmpl/delete.udf.tmpl` — the UDF delete confirmation
// ───────────────────────────────────────────────────────────────────────────

interface DeleteUdfPopupProps {
  /** `{{user_defined_field.name}}` is the only thing the template reads, which
   * is why this is structural rather than `IUserSettingsSubmittalUdf` — the
   * Proposal tab passes an `IUserSettingsPrpUdf`, and legacy passes both into
   * this same template. */
  field: { name: string | null };
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ★ Q10 — TWO COPY BUGS, BOTH REQUIRED.
 *  1. the heading reads "Delete **Use** Defined Field?" — no "r";
 *  2. the body says the field "will be deleted from all generated
 *     **Proposals**", on the SUBMITTAL tab, because this template is shared with
 *     the Proposal tab and was written for it.
 * Both are transcribed verbatim.
 *
 * ★ Q19 — the template's `mode == 'reset'` branch ("Reset Use Defined Field?",
 * `name="reset_field"`) is UNREACHABLE: `render_popup` only chooses `'reset'`
 * when the clicked element's className lacks `remove`, and the only bound
 * selectors are `button.remove` / `.reset`, of which `.reset` matches nothing in
 * any template. Proven dead (§10) and therefore not ported.
 */
export const DeleteUdfPopup = ({ field, onConfirm, onCancel }: DeleteUdfPopupProps) => (
  <div id="popup-content" className="popup-wrapper width-small delete-user-defined-field active">
    <div className="popup-content">
      <h2 className="popup-heading">Delete Use Defined Field?</h2>
      <div className="popup-content-part">
        <p>
          If you choose to Delete, all User Defined Field Data will be lost and will not be
          recoverable and will be deleted from all generated Proposals.
        </p>
        <ul className="deleted-items">
          <li>
            <p>{field.name}</p>
          </li>
        </ul>
      </div>
      <div className="popup-control-buttons">
        {/* The template's `{{#ifCompare}}` puts a space either side of the word:
            `> Delete <`. Kept. */}
        <button className="btn not-highlighted-btn" name="confirm_delete" onClick={onConfirm}>
          {' '}
          Delete{' '}
        </button>
        <button
          className="btn highlighted-btn"
          name="cancel_delete"
          data-parent=".delete-user-defined-field"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);

// ───────────────────────────────────────────────────────────────────────────
// `tmpl/delete.format.field.tmpl` — the FORMAT RAIL's delete confirmation (U-d)
// ───────────────────────────────────────────────────────────────────────────

interface DeleteFormatFieldPopupProps {
  /** `self.collectionSubmittalFormat.get(self.selected_field_id).toJSON()`
   * (`submittal.js:642`) / `self.collectionPrpFormat.get(self.delete_popup_for)
   * .toJSON()` (`proposal.js:463-465`) — only `name` is read. */
  format: { name: string | null };
  /** ★ `{{type}}` — the literal string the calling view passes in:
   * `'Submittal'` (`submittal.js:643`) or `'Proposal'` (`proposal.js:466`). It
   * appears twice in the template, once bare in the heading and once
   * PLURALISED in the body. Defaults to `'Submittal'` so the U-d call site is
   * unchanged. */
  type?: 'Submittal' | 'Proposal';
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * ★★ Q4 — THIS COMPONENT IS RENDERED TWICE PER CLICK, ON PURPOSE.
 *
 * `'click #submittal .delete'` matches BOTH the `<button class="… delete">` and
 * the `<i class="icon-trash delete">` nested inside it. jQuery fires a delegated
 * handler once per matching ancestor, and `render_delete_popup_submittal`
 * (unlike `render_popup`, `proposal.js:439`) never calls `stopPropagation` — so
 * ONE click appends TWO `#popup-content.delete-field.active` nodes with
 * identical text, stacked exactly on top of each other. Confirming or
 * cancelling clears both, because `remove_confirm_popup` (`:648-651`) removes
 * `#popup-content.delete-field` as a jQuery SET, and only ONE `DELETE` goes out.
 *
 * `SubmittalTab` therefore keeps an ARRAY of open popups, not a nullable single
 * one, and `FormatRail` puts the handler on both elements. Do not "fix" either.
 *
 * ★ `{{type}}` is the literal string the calling view passes in — `'Submittal'`
 * (`:643`) or, since U-e, `'Proposal'` (`proposal.js:466`) — so the heading
 * reads "Delete Submittal Field?" / "Delete Proposal Field?" and the body
 * "…will not appear in generated Submittals." / "…Proposals.". Unlike
 * `delete.udf.tmpl`, this one is NOT mis-worded; that is Q10, and it belongs to
 * the popup above.
 *
 * ★ Q20 lives on the trigger, not here: the thing that opens this is a
 * `data-toggle="dropdown"` Bootstrap toggle with no `.dropdown-menu` anywhere
 * near it, so it never opens a dropdown — it only ever fires this handler.
 */
export const DeleteFormatFieldPopup = ({
  format,
  type = 'Submittal',
  onConfirm,
  onCancel,
}: DeleteFormatFieldPopupProps) => (
  <div id="popup-content" className="popup-wrapper width-small delete-field active">
    <div className="popup-content">
      <h2 className="popup-heading">Delete {type} Field?</h2>
      <div className="popup-content-part">
        <p>If you choose to Delete, the Field data will not appear in generated {type}s.</p>
        <ul className="deleted-items">
          <li>
            {/* The template emits `{{format.name}} ` — trailing space. Kept. */}
            <p>{format.name} </p>
          </li>
        </ul>
      </div>
      <div className="popup-control-buttons">
        <button className="btn not-highlighted-btn" name="confirm_delete_field" onClick={onConfirm}>
          Delete
        </button>
        <button
          className="btn highlighted-btn"
          name="cancel_delete"
          data-parent=".delete-field"
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
);
